// src/App.tsx
import { useRoute, useSearchParams } from 'wouter';
import { useState, useEffect } from "react";
import { Container, Stack, Text, Loader, Group, Progress, Center, Image } from '@mantine/core';
import { useToggle, useTimeout, useSet } from '@mantine/hooks';
import "./App.css";
import type { TesteeDataObject, TestMeta } from '@/test.obj.ts';
// import { v7 as uuidv7 } from 'uuid';
import { GenTesteeId } from './id.gen.ts';
import StartPanel from './panels/test.start.tsx';
import ProfileCard from '@/componets/profile.card.tsx';
import TwoSidedPrompts from './panels/two.sided.prompts.tsx';
import SingleSidedPrompts from './panels/single.sided.prompts.tsx';
import FinalPanel from './panels/test.final.tsx';
import ResultsPanel from './panels/display.results.tsx';
import type { QnA_Block, OEJTresult } from '@/q-n-a.ts';
import { useAuthExtrasHook } from '@/hooks/auth.extras.tsx';
import { useBrandTheme } from '@/contexts/brander.ctx.tsx';
function App() {
  const { setBranding } = useBrandTheme();
  const [match, params] = useRoute('/:mode/:tid');
  const [searchParams, _] = useSearchParams();
  const [metaInfo, setMetaInfo] = useState<TestMeta | null>(null);
  const [testObj, setTestObj] = useState<Partial<TesteeDataObject> | null>(null);
  const [uiStep, toggleStep] = useToggle(['start', 'testPhase1', 'testPhase2', 'testPhase3', 'exitQuestions', 'testSubmit']);
  const [currentTestSet, setCurrentTestSet] = useState<QnA_Block[]>([]);
  const [testResult, setTestResult] = useState<OEJTresult | null>(null);
  const [strType, setStrType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { start } = useTimeout(() => setLoading(false), 100);
  const [isForAccessor, setIsForAccessor] = useState(false);
  const [imgSrc, setImgSrc] = useState<string>('');
  const [resultImgUrl, setResultImgUrl] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const [pageMode, setPageMode] = useState<'test' | 'result'>('test');
  const [postResults, setPostResults] = useState<boolean>(false); // If true, then the test id should be posted to the parent window
  const [testID, setTestID] = useState<string>('');
  const [testAccessID, setTestAccessID] = useState<string>('');
  const progressSet = useSet<string | number>([]);
  const [currentProgress, setCurrentProgress] = useState(0);

  const { injectHeader, hasToken } = useAuthExtrasHook();

  useEffect(() => {
    // Sets up the page mode based on the url
    if (match) {
      setPageMode(params.mode === 't' ? 'test' : 'result'); // mode can be 't' or 'tr'
      params.mode === 't' ? setTestAccessID(params.tid) : setTestID(params.tid);
    }
    setIsForAccessor(!!searchParams.get('foraccessor'));
    setPostResults(!!searchParams.get('postresults'));
  }, []);

  useEffect(() => {
    // Gets the test results
    if (!!testID && pageMode === 'result') {
      setLoading(true);
      toggleStep('testSubmit');
      fetch(`/api/results/${testID}`)
        .then(async res => {
          const data = await res.json() as { breakdown: OEJTresult, rstr: string, accessorID: string, imgUrl: string };
          setTestResult(data.breakdown);
          setStrType(data.rstr);
          setTestAccessID(data.accessorID);
          setResultImgUrl(data.imgUrl);
        })
        .catch(err => {
          console.error('testSubmit::error', err);
        })
        .finally(() => setLoading(false));
    }
  }, [testID, pageMode])

  useEffect(() => {
    /* This does...
      - Get the profile image
      - If in test mode, then:
        - Get the first set of questions
        - Create the test object
      - Get the test metadata (Agent/team)
     */
    if (!testAccessID) return; // If no test access id, then we don't need to do this
    fetch(`/api/${testAccessID}/img`)
      .then(async res => {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setImgSrc(url);
      });
    if (pageMode === 'test') {
      // Get the first set of questions
      fetch('/api/questionset/oejts-pt1')
        .then(async res => {
          const qd = await res.json() as { questions: QnA_Block[] };
          setCurrentTestSet(qd.questions);
        });
      const testObj = {
        testeeID: GenTesteeId(),
        testAccessID,
      }
      setTestObj(testObj);
    }

    fetch(`/api/testmeta/${testAccessID}`)
      .then(async res => {
        const data = await res.json() as TestMeta;
        setMetaInfo(data);
        if(data.branding){
          setBranding({
            colorCode: data.branding.colorCode,
            logoUrl: data.branding.logoUrl
          });
          setLogoUrl(data.branding.logoUrl ?? null);
        }
      })

  }, [testAccessID, pageMode])

  useEffect(() => {
    // Handles the submission of the test results to the server
    if (uiStep === 'testSubmit' && pageMode === 'test') {
      setLoading(true);
      console.log('testSubmit::hit - sending data to server', testObj);
      const req = new Request(`/api/results`, {
        method: 'POST',
        body: JSON.stringify(testObj),
      });
      if(hasToken) {
        console.log('testSubmit::hit - injecting token header');
        injectHeader(req);
      }
      fetch(req)
        .then(res => res.json())
        .then((data: { results: OEJTresult, strType: string, tid: string }) => {
          if (postResults) {
            window.parent.postMessage({testID: data.tid, from: 'myyenta'});
            return;
          }
          setTestResult(data.results);
          setStrType(data.strType);
          setTestID(data.tid);
        })
        .catch(err => {
          console.error('testSubmit::error', err);
        })
        .finally(() => start());
    }
  }, [uiStep]);

  const getNextQuestionSet = async () => {
    const url = uiStep === 'testPhase2' ? '/api/questionset/oejts-pt2' : '/api/questionset/factor2';
    const res = await fetch(url);
    const qd = await res.json() as { questions: QnA_Block[] };
    setCurrentTestSet(qd.questions);
  }

  // Next for the test question panels
  const handleNext = (qaData: QnA_Block[], nxtStep: typeof uiStep) => {
    if (uiStep !== 'testPhase1' && uiStep !== 'testPhase2' && uiStep !== 'testPhase3') {
      console.log('handleNext::hit - but the phase is not correct', uiStep, testObj);
      return;
    }
    const testData = testObj?.testData ? [...testObj.testData, ...qaData] : qaData;
    setTestObj(prev => ({ ...prev, testData }));
    setLoading(true);
    toggleStep(nxtStep);
    getNextQuestionSet()
      .catch(err => {
        console.error('getNextQuestionSet::error', err);
      })
      .finally(() => start());

  }

  const handleProgress = (qid: string | number) => {
    progressSet.add(qid);
    setCurrentProgress((progressSet.size/metaInfo!.questionCount)*100);
  }

  if (!match) return (
    <Container fluid p={4}>
      <Stack justify='center'>
        <Text size='xl' fw={600}>
          Your test link is invalid.
        </Text>
      </Stack>
    </Container>
  )

  return (
    <Container fluid p={4} w={{ base: '85vw', md: uiStep === 'testSubmit' ? '90vw' : 600 }} ta='center' >
      <Stack justify='center'>
        {(match && metaInfo?.metaProfile.showProfileCard) && <ProfileCard
          profileImg={imgSrc}
          profileName={metaInfo?.name ?? ''}
          profilePersonality={metaInfo?.pType ?? 'N/A'}
          profileAbout={metaInfo?.flavorTxt ?? ''}
          profileContact={{ email: metaInfo?.email ?? '', phone: metaInfo?.phone ?? '' }}
          servicing={metaInfo?.areasCovered ?? []}
          profileCompany={metaInfo?.teamName || metaInfo?.teamWebsite || metaInfo?.website || ''}
          companyLink={metaInfo?.teamWebsite || metaInfo?.website || ''}
        />}
        {(!!metaInfo && (uiStep === 'testPhase1' || uiStep === 'testPhase2' || uiStep === 'testPhase3')) && (<Progress value={currentProgress} radius={24} />)}
        {loading && (<Group justify='center'><Loader /></Group>)}
        {(loading && uiStep === 'testSubmit') && (<Text size='xl' fw={600}>Getting your results...</Text>)}
        {(uiStep === 'start' && !loading) && (<StartPanel onNext={tio => {
          setTestObj(prev => ({ ...prev, testeeInfo: tio }));
          toggleStep('testPhase1');
        }} initObj={testObj?.testeeInfo} />)}
        {(uiStep === 'testPhase1' && !loading) && (
          <TwoSidedPrompts questions={currentTestSet} onSubmit={qaData => handleNext(qaData, 'testPhase2')} onProgress={handleProgress} />
        )}
        {((uiStep === 'testPhase2' || uiStep === 'testPhase3') && !loading) && (() => {
          return (
            <SingleSidedPrompts
              questions={currentTestSet}
              onSubmit={qaData => handleNext(qaData, uiStep === 'testPhase3' ? 'exitQuestions' : 'testPhase3')}
              onProgress={handleProgress}
            />
          );
        })()}
        {(uiStep === 'exitQuestions' && !loading) && (<FinalPanel questions={metaInfo?.metaProfile.exitQuestions || []} onNext={data => {
          setTestObj(prev => ({ ...prev, exitQuestions: data }));
          toggleStep('testSubmit');
        }} />)}
        {(uiStep === 'testSubmit' && !loading && !!testResult) && (<ResultsPanel results={testResult as OEJTresult} strType={strType} imgUrl={resultImgUrl} resultUrl={`https://${window.location.host}/tr/${testID}`} />)}
        {logoUrl && (<Center><Image src={logoUrl} alt='Logo' width={100} height={100} fit='contain' /></Center>)}
      </Stack>

    </Container>
  );
}

export default App;
